import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { MuiThemeProvider, withStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import CustomAppBar from "./pages/CustomAppBar";
import GlobalContext from "./GlobalContext";
import constants from "./tools/constants";
import MainRouter from "./pages/MainRouter";
import * as authStorage from "./tools/authStorage";
import theme from "./theme";
import UserType from "./types/User";
import jwt_decode from "jwt-decode";
import { Provider } from "mobx-react";
import { stores } from "store";
import packageInfo from "../package.json";

interface TranslationType {
  [key: string]: string;
}

type AuthJwtPayload = {
  auth: string;
  exp: number;
  sub: string;
};

const translations: TranslationType = {
  Unauthorized: "Connexion refusée",
};

(window as any).appVersion = packageInfo.version;

const styles = (thm: Theme) => {
  return createStyles({
    content: {
      display: "flex",
      width: "100%",
      flexGrow: 1,
      // backgroundColor: "blue"
    },
    contentScrollable: {
      display: "block",
      width: "100%",
      flexGrow: 1,
      height: "calc(100vh)",
      overflowY: "auto",
      overflowX: "hidden",
    },
    padded: {
      padding: `0 ${thm.spacing.unit * 2}px ${thm.spacing.unit * 2}px ${thm
        .spacing.unit * 2}px`,
    },
    fullPadded: {
      paddingTop: `${thm.spacing.unit * 2}px`,
    },
  });
};

type AppContentProps = {
  classes: Record<string, string>;
  user: any;
  handleSubmitLogin: (username: string, password: string) => Promise<any>;
  onSubmitVerify: (code: string) => Promise<any>;
  getQrCode: () => Promise<any>;
  handleLogout: () => void;
};

const AppContent = ({
  classes,
  user,
  handleSubmitLogin,
  onSubmitVerify,
  getQrCode,
  handleLogout,
}: AppContentProps) => {
  const { pathname } = useLocation();
  const noPadding =
    pathname.search("login") !== -1 ||
    (pathname.search("password") !== -1 && !user) ||
    pathname.search("qrcode") !== -1 ||
    pathname.search("verifyCode") !== -1;

  const fullPadded = pathname.search("mosaic") !== -1;
  const contentScrollable =
    pathname === "/" ||
    /^\/projects\/[^/]+\/import\/dossierlocal\/?$/.test(pathname) ||
    /^\/analyse(\/[^/]+)?\/?$/.test(pathname) ||
    /^\/projects\/[^/]+\/import\/.+/.test(pathname);

  return (
    <main
      className={classNames(classes.content, {
        [classes.padded]: !noPadding,
        [classes.fullPadded]: fullPadded,
        [classes.contentScrollable]: contentScrollable,
      })}
    >
      <MainRouter
        handleSubmitLogin={handleSubmitLogin}
        onSubmitVerify={onSubmitVerify}
        getQrCode={getQrCode}
        user={user}
        handleLogout={handleLogout}
      />
    </main>
  );
};

const App = (props: any) => {
  const { classes } = props;
  const [user, setUser] = useState<any>(null);
  const [triedUserFromStorage, setTriedUserFromStorage] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [isAppVersionLoading, setIsAppVersionLoading] = useState(true);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    id: "",
  });

  const handleLogout = () => {
    authStorage.clearUser();
    setUser(null);
  };

  const appFetch = async (
    url: any,
    options?: any,
    respWithHeader?: boolean,
    fake?: boolean,
  ) => {
    if (user && user.exp && user.exp < Date.now() / 1000) {
      handleLogout();
    }
    const auth = user ? { Authorization: "Bearer " + user.token } : {};
    let headers = {
      "Content-Type": "application/json",
      ...auth,
    };

    const res = await fetch(
      `${
        fake
          ? process.env.REACT_APP_API_URL_FAKE
          : process.env.REACT_APP_API_URL
      }${url}`,
      {
        ...options,
        headers: {
          ...headers,
          ...(options && options.headers ? options.headers : {}),
        },
      },
    );
    if (!res.ok) {
      const responseText = await res.text();
      const translatedMessage = translations[res.statusText] || res.statusText;
      const fallbackMessage = `HTTP ${res.status}`;
      const appError: any = new Error(translatedMessage || fallbackMessage);
      appError.status = res.status;
      appError.statusText = res.statusText;
      appError.responseText = responseText;
      throw appError;
    }

    const contentType = res.headers.get("Content-Type") || "";
    if (res.status === 200 && contentType === "image/png") {
      return res;
    }
    if (res.status !== 204 && contentType.indexOf("application/json") !== -1) {
      if (respWithHeader) {
        return {
          headers: res.headers,
          response: res.json(),
        };
      }
      return res.json();
    }

    if (respWithHeader) {
      return {
        headers: res.headers,
        response: res.text(),
      };
    }
    return res.text();
  };

  const fetchAppVersion = async (): Promise<string> => {
    const fallbackVersion = "1.0.0";
    try {
      const config: any = await appFetch(constants.paths.APP_CONFIG, {
        method: "GET",
      });
      return typeof config.version === "string" && config.version
        ? config.version
        : fallbackVersion;
    } catch {
      return fallbackVersion;
    }
  };

  const handleSubmitLogin = async (username: string, password: string) => {
    const res = await appFetch(
      constants.paths.AUTH_PATH,
      {
        method: "POST",
        body: JSON.stringify({ username, password }),
      },
      true,
    );
    setCredentials({
      username: username,
      password: password,
      id: res.headers.get("userId"),
    });
    // on renvoie le lien de redirection en fonction de si l'user
    // a deja configuré le 2FA ou non
    return res.headers.get("link");
  };

  const handleSubmitVerify = async (code: string) => {
    if (credentials.username === "") {
      window.location.replace("/login");
    }
    try {
      const res: any = await appFetch(`${constants.paths.VERIFY_CODE_PATH}`, {
        method: "POST",
        body: JSON.stringify({
          id: credentials.id,
          username: credentials.username,
          password: credentials.password,
          code: code,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      const { auth, exp, sub } = jwt_decode<AuthJwtPayload>(res.id_token);
      const userInfos: UserType = await appFetch(
        `${constants.paths.USERS_PATH}/${sub}`,
        {
          headers: {
            Authorization: "Bearer " + res.id_token,
            "Content-Type": "application/json",
          },
        },
      );

      setUser({
        ...user,
        ...userInfos,
        authorities: [auth],
        exp,
        token: res.id_token,
      });
      authStorage.storeUser({
        ...user,
        ...userInfos,
        authorities: [auth],
        exp,
        token: res.id_token,
      });

      // on renvoie les droits
      return auth;
    } catch (err) {
      console.log(err);
      return err;
    }
  };

  function blobToBase64(blob: any) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  const getQrCode = () => {
    if (credentials.username === "") {
      window.location.replace("/login");
    }
    return appFetch(`${constants.paths.QRCODE_PATH}`, {
      method: "POST",
      body: JSON.stringify({
        username: credentials.username,
        password: credentials.password,
      }),
    })
      .then((resp) => {
        return resp.blob();
      })
      .then(async (blob) => {
        const dataImage = await blobToBase64(blob);
        const urlImage = await URL.createObjectURL(blob);
        return { data: dataImage, url: urlImage };
      });
  };

  const updateUserAuth = (userInfos: any) => {
    const usr = authStorage.getUser();
    setUser({
      ...usr,
      ...userInfos,
    });
    authStorage.storeUser({
      ...usr,
      ...userInfos,
    });
  };

  useEffect(() => {
    const usr = authStorage.getUser();

    // dans le router la redirecttion vers login intervenait
    // avant qu'on ait eu le temps d'essayer charger l'utilisateur depuis localStorage
    setTriedUserFromStorage(true);

    if (usr) {
      setUser(usr);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetchAppVersion()
      .then((version) => {
        if (isMounted) setAppVersion(version);
      })
      .finally(() => {
        if (isMounted) setIsAppVersionLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!triedUserFromStorage) {
    return null;
  }

  return (
    <Provider {...stores}>
      <CssBaseline />
      <Router>
        <MuiThemeProvider theme={theme}>
          <GlobalContext.Provider
            value={{
              user,
              appFetch,
              updateUserAuth,
              setUser,
              appVersion,
              isAppVersionLoading,
            }}
          >
            <CustomAppBar user={user} handleLogout={handleLogout} />
            <AppContent
              classes={classes}
              user={user}
              handleSubmitLogin={handleSubmitLogin}
              onSubmitVerify={handleSubmitVerify}
              getQrCode={getQrCode}
              handleLogout={handleLogout}
            />
          </GlobalContext.Provider>
        </MuiThemeProvider>
      </Router>
    </Provider>
  );
};

export default withStyles(styles)(App);
